import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography
} from "@material-ui/core";

import {
  CheckCircleOutline,
  CloudDone,
  Facebook,
  KeyboardArrowDown,
  KeyboardArrowUp,
  VpnKey
} from "@material-ui/icons";

import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useMetaEmbeddedSignup } from "../../meta";

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: 20,
    overflow: "hidden"
  },

  header: {
    position: "relative",
    overflow: "hidden",
    padding: "24px 26px",
    color: "#FFFFFF",
    background:
      "linear-gradient(135deg,#071329 0%,#0B2C68 52%,#155EEF 100%)"
  },

  headerIcon: {
    width: 50,
    height: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 15,
    color: "#BFDBFE",
    background: "rgba(255,255,255,.11)",
    border: "1px solid rgba(255,255,255,.14)"
  },

  title: {
    marginTop: 14,
    fontSize: "1.25rem",
    fontWeight: 900,
    letterSpacing: "-.03em"
  },

  subtitle: {
    maxWidth: 560,
    marginTop: 7,
    color: "rgba(219,234,254,.75)",
    fontSize: "0.75rem",
    lineHeight: 1.6
  },

  content: {
    padding: "24px 26px"
  },

  informationBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: 13,
    marginBottom: 18,
    borderRadius: 12,
    color:
      theme.palette.type === "dark"
        ? "#BFDBFE"
        : "#1E40AF",
    background:
      theme.palette.type === "dark"
        ? "rgba(37,99,235,.10)"
        : "#EFF6FF",
    border: "1px solid rgba(37,99,235,.13)",
    fontSize: "0.7rem",
    lineHeight: 1.55
  },

  actions: {
    padding: "16px 26px 22px"
  },

  secondaryButton: {
    minHeight: 40,
    borderRadius: 10,
    fontWeight: 800,
    textTransform: "none"
  },

  primaryButton: {
    minHeight: 40,
    borderRadius: 10,
    color: "#FFFFFF",
    background:
      "linear-gradient(135deg,#2563EB,#4F46E5)",
    fontWeight: 850,
    textTransform: "none",
    boxShadow:
      "0 10px 22px rgba(37,99,235,.22)"
  },

  statusBox: {
    padding: 15,
    marginTop: 18,
    borderRadius: 13,
    color:
      theme.palette.type === "dark"
        ? "#BBF7D0"
        : "#166534",
    background:
      theme.palette.type === "dark"
        ? "rgba(34,197,94,.08)"
        : "#F0FDF4",
    border: "1px solid rgba(34,197,94,.18)"
  },

  officialCard: {
    padding: 18,
    borderRadius: 15,
    border: "1px solid rgba(37,99,235,.15)",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(145deg,rgba(37,99,235,.10),rgba(15,23,42,.35))"
        : "linear-gradient(145deg,#EFF6FF,#FFFFFF)",
    boxShadow: "0 8px 22px rgba(37,99,235,.07)"
  },

  officialButton: {
    minHeight: 44,
    marginTop: 16,
    borderRadius: 11,
    color: "#FFFFFF",
    background: "linear-gradient(135deg,#1877F2,#2563EB)",
    fontWeight: 850,
    textTransform: "none",
    boxShadow: "0 8px 18px rgba(37,99,235,.20)"
  },

  advancedToggle: {
    width: "100%",
    marginTop: 16,
    padding: "10px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    background: "transparent",
    color: theme.palette.text.secondary,
    cursor: "pointer",
    fontWeight: 750,
    fontSize: "0.75rem"
  }
}));

const initialForm = {
  name: "",
  phoneNumberId: "",
  wabaId: "",
  businessId: "",
  accessToken: "",
  appSecret: "",
  verifyToken: ""
};
const MetaCloudModal = ({
  open,
  onClose,
  onSuccess
}) => {
  const classes = useStyles();

  const {
    ready: metaReady,
    sdkLoading,
    sdkError,
    connecting,
    connect
  } = useMetaEmbeddedSignup();

  const [form, setForm] =
    useState(initialForm);

  const [advancedOpen, setAdvancedOpen] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [validated, setValidated] =
    useState(null);

  useEffect(() => {
    if (!open) {
      setForm(initialForm);
      setValidated(null);
      setSaving(false);
      setAdvancedOpen(false);
    }
  }, [open]);

  const handleChange = event => {
    const {
      name,
      value
    } = event.target;

    setForm(previous => ({
      ...previous,
      [name]: value
    }));
  };

  const validateForm = () => {
    const required = [
      "name",
      "phoneNumberId",
      "wabaId",
      "accessToken",
      "appSecret",
      "verifyToken"
    ];

    const missing =
      required.filter(
        field =>
          !String(
            form[field] || ""
          ).trim()
      );

    if (missing.length > 0) {
      toast.error(
        "Preencha todos os campos obrigatórios."
      );

      return false;
    }

    return true;
  };

  const handleEmbeddedSignup = async () => {
    if (saving || connecting) {
      return;
    }

    setSaving(true);
    setValidated(null);

    try {
      const result =
        await connect();

      const connectionResponse =
        await api.post(
          "/whatsapp",
          {
            name:
              "WhatsApp Oficial Meta",
            provider:
              "META_CLOUD",
            channel:
              "whatsapp",
            status:
              "DISCONNECTED",
            queueIds: []
          }
        );

      const whatsappId =
        connectionResponse.data.id;

      const completionResponse =
        await api.post(
          `/meta-cloud/${whatsappId}/embedded-signup`,
          {
            code:
              result.code,
            phoneNumberId:
              result.session
                ?.phoneNumberId,
            wabaId:
              result.session
                ?.wabaId,
            businessId:
              result.session
                ?.businessId
          }
        );

      setValidated(
        completionResponse.data
          ?.connection || {}
      );

      toast.success(
        "WhatsApp Oficial conectado com sucesso."
      );

      if (
        typeof onSuccess ===
        "function"
      ) {
        onSuccess({
          whatsappId,
          connection:
            completionResponse.data
              ?.connection
        });
      }
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (
      saving ||
      !validateForm()
    ) {
      return;
    }

    setSaving(true);
    setValidated(null);

    try {
      const connectionResponse =
        await api.post(
          "/whatsapp",
          {
            name:
              form.name.trim(),
            provider:
              "META_CLOUD",
            channel:
              "whatsapp",
            status:
              "DISCONNECTED",
            queueIds: []
          }
        );

      const whatsappId =
        connectionResponse.data.id;

      await api.put(
        `/meta-cloud/${whatsappId}/credentials`,
        {
          phoneNumberId:
            form.phoneNumberId.trim(),
          wabaId:
            form.wabaId.trim(),
          businessId:
            form.businessId.trim(),
          accessToken:
            form.accessToken.trim(),
          appSecret:
            form.appSecret.trim(),
          verifyToken:
            form.verifyToken.trim()
        }
      );

      const validationResponse =
        await api.post(
          `/meta-cloud/${whatsappId}/validate`
        );

      setValidated(
        validationResponse.data
          ?.connection || {}
      );

      toast.success(
        "WhatsApp Oficial conectado com sucesso."
      );

      if (
        typeof onSuccess ===
        "function"
      ) {
        onSuccess({
          whatsappId,
          connection:
            validationResponse.data
              ?.connection
        });
      }
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) {
      return;
    }

    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: classes.paper
      }}
    >
      <Box className={classes.header}>
        <Box className={classes.headerIcon}>
          <CloudDone />
        </Box>

        <Typography className={classes.title}>
          Conectar WhatsApp Oficial
        </Typography>

        <Typography className={classes.subtitle}>
          Utilize a API Oficial da Meta para operar sem QR
          Code, com mais estabilidade, segurança e
          capacidade de escala.
        </Typography>
      </Box>

      <DialogTitle style={{ display: "none" }}>
        WhatsApp Oficial
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Box className={classes.officialCard}>
          <Box
            display="flex"
            alignItems="flex-start"
            style={{ gap: 12 }}
          >
            <Box
              style={{
                width: 42,
                height: 42,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 12,
                color: "#FFFFFF",
                background:
                  "linear-gradient(135deg,#1877F2,#2563EB)"
              }}
            >
              <Facebook />
            </Box>

            <Box style={{ minWidth: 0 }}>
              <Typography
                style={{
                  fontWeight: 900,
                  fontSize: "0.95rem"
                }}
              >
                Conectar com a Meta
              </Typography>

              <Typography
                color="textSecondary"
                style={{
                  marginTop: 4,
                  fontSize: "0.72rem",
                  lineHeight: 1.55
                }}
              >
                Entre com sua conta da Meta e conecte seu
                WhatsApp Business Oficial em poucos passos,
                sem copiar tokens ou identificadores.
              </Typography>
            </Box>
          </Box>

          <Button
            fullWidth
            className={classes.officialButton}
            onClick={handleEmbeddedSignup}
            disabled={
              saving ||
              connecting ||
              sdkLoading ||
              !metaReady
            }
            startIcon={
              saving || connecting
                ? (
                  <CircularProgress
                    size={17}
                    style={{ color: "#FFFFFF" }}
                  />
                )
                : <Facebook />
            }
          >
            {sdkLoading
              ? "Carregando Meta..."
              : connecting || saving
              ? "Conectando..."
              : sdkError
              ? "Meta indisponível"
              : "Continuar com a Meta"}
          </Button>

          {sdkError && (
            <Typography
              style={{
                marginTop: 9,
                color: "#B91C1C",
                fontSize: "0.68rem",
                lineHeight: 1.45
              }}
            >
              {sdkError.message}
            </Typography>
          )}
        </Box>

        <button
          type="button"
          className={classes.advancedToggle}
          onClick={() =>
            setAdvancedOpen(current => !current)
          }
        >
          <span>Configuração manual avançada</span>

          {advancedOpen
            ? <KeyboardArrowUp fontSize="small" />
            : <KeyboardArrowDown fontSize="small" />}
        </button>

        {advancedOpen && (
          <>
            <Box
              className={classes.informationBox}
              style={{ marginTop: 14 }}
            >
          <VpnKey
            style={{
              marginTop: 1,
              fontSize: 18
            }}
          />

          <span>
            As credenciais serão enviadas diretamente ao
            servidor e armazenadas de forma criptografada.
            Tokens e segredos não serão exibidos novamente.
          </span>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="Nome da conexão"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Ex.: WhatsApp Oficial Comercial"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="Phone Number ID"
              name="phoneNumberId"
              value={form.phoneNumberId}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="WABA ID"
              name="wabaId"
              value={form.wabaId}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="Business ID (opcional)"
              name="businessId"
              value={form.businessId}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="Access Token"
              name="accessToken"
              value={form.accessToken}
              onChange={handleChange}
              type="password"
              autoComplete="new-password"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="App Secret"
              name="appSecret"
              value={form.appSecret}
              onChange={handleChange}
              type="password"
              autoComplete="new-password"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="Verify Token"
              name="verifyToken"
              value={form.verifyToken}
              onChange={handleChange}
              type="password"
              autoComplete="new-password"
            />
          </Grid>
        </Grid>
          </>
        )}

        {validated && (
          <Box className={classes.statusBox}>
            <Box
              display="flex"
              alignItems="center"
              style={{ gap: 8 }}
            >
              <CheckCircleOutline />
              <strong>Conexão validada</strong>
            </Box>

            <Typography
              style={{
                marginTop: 7,
                fontSize: "0.7rem",
                lineHeight: 1.55
              }}
            >
              {validated.verifiedName
                ? `Empresa verificada: ${validated.verifiedName}`
                : "Credenciais confirmadas pela Meta."}

              {validated.displayPhoneNumber
                ? ` Número: ${validated.displayPhoneNumber}.`
                : ""}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={handleClose}
          disabled={saving}
          className={classes.secondaryButton}
        >
          Fechar
        </Button>

        {advancedOpen && (
          <Button
            onClick={handleSave}
            disabled={
              saving ||
              Boolean(validated)
            }
            className={classes.primaryButton}
          >
            {saving && (
              <CircularProgress
                size={17}
                style={{
                  marginRight: 8,
                  color: "#FFFFFF"
                }}
              />
            )}

            {saving
              ? "Salvando e validando..."
              : validated
              ? "Conexão validada"
              : "Salvar manualmente"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MetaCloudModal;
